import React, { useEffect, useRef } from 'react';
import { useMusicStore, MUSIC_TRACKS } from '../../store/musicStore';
import { useAcademyStore } from '../../store/academyStore';
import { useSfx } from '../../store/sfxStore';
import { Play, Pause, SkipForward, Volume2, VolumeX, Music, Radio } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const RadioBar = () => {
    const { currentTrack, isPlaying, volume, isMuted, togglePlay, setVolume, toggleMute, playTrack } = useMusicStore();
    const { currentUser, progress, settings, toggleVoice } = useAcademyStore();
    const { isMuted: sfxMuted, toggleMute: toggleSfxMute, play } = useSfx();
    const audioRef = useRef<HTMLAudioElement>(null);
    const [showPlaylist, setShowPlaylist] = React.useState(false);
    const [isExpanded, setIsExpanded] = React.useState(true);

    // Calculate Available Tracks
    const userProgress = currentUser ? progress[currentUser.id] : null;
    const unlockedCourses = userProgress?.unlockedCourses || [];

    const availableTracks = MUSIC_TRACKS.filter(t =>
        !t.courseId || unlockedCourses.includes(t.courseId)
    );

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = isMuted ? 0 : volume;
        }
    }, [volume, isMuted]);

    useEffect(() => {
        if (audioRef.current) {
            if (isPlaying) {
                const playPromise = audioRef.current.play();
                if (playPromise !== undefined) {
                    playPromise.catch(error => {
                        console.log("Audio autoplay prevented:", error);
                    });
                }
            } else {
                audioRef.current.pause();
            }
        }
    }, [isPlaying, currentTrack]);

    return (
        <>
            {/* Hidden Audio Element - Always Active */}
            <audio
                ref={audioRef}
                src={currentTrack.src}
                loop
                muted={isMuted} // Stops instant play on load before react catches up
            />

            {/* Visual Interface - Only shown when logged in */}
            {currentUser && (
                <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2 print:hidden">
                    <AnimatePresence>
                        {/* Collapsed State: Mini Player Button */}
                        {!isExpanded && (
                            <motion.button
                                initial={{ scale: 0, rotate: -90 }}
                                animate={{ scale: 1, rotate: 0 }}
                                exit={{ scale: 0, rotate: -90 }}
                                onClick={() => { setIsExpanded(true); play('click'); }}
                                className={`p-4 rounded-full shadow-2xl transition-all border-4 ${isPlaying ? 'bg-green-500 border-green-300 animate-pulse' : 'bg-slate-800 border-slate-600'}`}
                            >
                                <Music className="text-white" size={24} />
                            </motion.button>
                        )}

                        {/* Expanded State: Full Player */}
                        {isExpanded && (
                            <motion.div
                                initial={{ opacity: 0, y: 50, scale: 0.8 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 50, scale: 0.8 }}
                                className="bg-slate-900/95 backdrop-blur-xl text-white p-4 rounded-3xl border border-white/10 shadow-2xl w-full max-w-sm"
                            >
                                {/* Header with Minimize */}
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${isPlaying ? 'bg-green-500 animate-ping' : 'bg-slate-600'}`} />
                                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Gorilla Radio</span>
                                    </div>
                                    <button
                                        onClick={() => { setIsExpanded(false); play('click'); }}
                                        className="text-slate-500 hover:text-white transition-colors"
                                    >
                                        <VolumeX size={16} className="rotate-45" /> {/* Use X icon as close/minimize */}
                                    </button>
                                </div>

                                {/* Track Info & Playlist Toggle */}
                                <div className="relative mb-4">
                                    <button
                                        onClick={() => { setShowPlaylist(!showPlaylist); play('click'); }}
                                        className="w-full text-left bg-slate-800/50 hover:bg-slate-800 rounded-xl p-3 border border-slate-700/50 transition-all group"
                                    >
                                        <div className="text-sm font-bold truncate pr-6">{currentTrack.title}</div>
                                        <div className="text-xs text-slate-400">Tap to change station</div>
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 group-hover:text-white">
                                            <Music size={16} />
                                        </div>
                                    </button>

                                    {/* Playlist Dropdown */}
                                    <AnimatePresence>
                                        {showPlaylist && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10, height: 0 }}
                                                animate={{ opacity: 1, y: 0, height: 'auto' }}
                                                exit={{ opacity: 0, y: 10, height: 0 }}
                                                className="absolute bottom-full left-0 mb-2 w-full bg-slate-800 rounded-xl border border-slate-700 shadow-xl overflow-hidden z-20"
                                            >
                                                <div className="max-h-60 overflow-y-auto p-1 text-slate-300">
                                                    {availableTracks.map(track => (
                                                        <button
                                                            key={track.id}
                                                            onClick={() => {
                                                                playTrack(track.id);
                                                                setShowPlaylist(false);
                                                                play('click');
                                                            }}
                                                            className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium flex items-center gap-3 ${currentTrack.id === track.id ? 'bg-green-600/20 text-green-400' : 'hover:bg-white/5'}`}
                                                        >
                                                            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                                                            <span className="truncate flex-1">{track.title}</span>
                                                            {currentTrack.id === track.id && <Radio size={14} />}
                                                        </button>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Controls */}
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => { togglePlay(); play('click'); }}
                                            className="w-12 h-12 rounded-full bg-white text-slate-900 flex items-center justify-center hover:scale-105 active:scale-95 transition-all shadow-lg shadow-white/10"
                                        >
                                            {isPlaying ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" className="ml-1" />}
                                        </button>

                                        <div className="flex-1">
                                            <div className="flex flex-col gap-1">
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="1"
                                                    step="0.05"
                                                    value={volume}
                                                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                                                    className="w-24 h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-green-500 hover:accent-green-400"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <button onClick={() => { toggleMute(); play('click'); }} className={`p-2 rounded-xl transition-all ${isMuted ? 'text-red-400 bg-red-400/10' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                                        {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                                    </button>
                                </div>
                                
                                {/* Additional Audio Controls (SFX & Narrator) */}
                                <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-700/50">
                                    <button
                                        onClick={() => { toggleSfxMute(); play('click'); }}
                                        className={`flex flex-col items-center justify-center p-2 rounded-xl border transition-colors ${sfxMuted ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-slate-800/50 border-slate-700 hover:bg-slate-700 text-slate-300'}`}
                                    >
                                        {sfxMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                                        <span className="text-[10px] font-bold mt-1 uppercase tracking-wider">SFX</span>
                                    </button>

                                    <button
                                        onClick={() => { toggleVoice(); play('click'); }}
                                        className={`flex flex-col items-center justify-center p-2 rounded-xl border transition-colors ${!settings.voiceEnabled ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-slate-800/50 border-slate-700 hover:bg-slate-700 text-slate-300'}`}
                                    >
                                        {!settings.voiceEnabled ? <VolumeX size={18} /> : <Volume2 size={18} />}
                                        <span className="text-[10px] font-bold mt-1 uppercase tracking-wider">Voice</span>
                                    </button>

                                    <button
                                        onClick={() => { 
                                            const allMuted = sfxMuted && isMuted && !settings.voiceEnabled;
                                            if (allMuted) {
                                                if (sfxMuted) toggleSfxMute();
                                                if (isMuted) toggleMute();
                                                if (!settings.voiceEnabled) toggleVoice();
                                            } else {
                                                if (!sfxMuted) toggleSfxMute();
                                                if (!isMuted) toggleMute();
                                                if (settings.voiceEnabled) toggleVoice();
                                            }
                                            play('click'); 
                                        }}
                                        className="flex flex-col items-center justify-center p-2 rounded-xl border bg-slate-800/50 border-slate-700 hover:bg-red-500/20 hover:border-red-500/50 hover:text-red-400 text-slate-300 transition-colors"
                                    >
                                        {(sfxMuted && isMuted && !settings.voiceEnabled) ? <Volume2 size={18} /> : <VolumeX size={18} />}
                                        <span className="text-[10px] font-bold mt-1 uppercase tracking-wider">
                                            {(sfxMuted && isMuted && !settings.voiceEnabled) ? 'Unmute' : 'Mute All'}
                                        </span>
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}
        </>
    );
};
